import { makeAutoObservable } from 'mobx'
import {
  type AnalyticsChartPeriod,
  type ICreditUsageChartResponse,
  type ICreditUsageStatisticResponse,
  type ProcessedStatistic,
} from 'entities/Analytics/api/types'
import { computeHasStatistic, formatDateForDisplay } from 'pages/analytics/lib'

export type CreditUsageRechartsDataPoint = {
  date: string
  name: string
  total_credits: number
  transcripts_credits: number
  chatbots_credits: number
  ai_calling_agent_credits: number
  calls_credits: number
  messages_credits: number
  rvm_credits: number
}

export class CreditUsageChartDataStore {
  private _rawStatistic: ICreditUsageStatisticResponse[] = []
  private _rawChartData: ICreditUsageChartResponse[] = []
  private _period: AnalyticsChartPeriod = 'week'

  constructor() {
    makeAutoObservable(this)
  }

  setRawData = (
    response: { statistic: ICreditUsageStatisticResponse[]; charts: ICreditUsageChartResponse[] },
    period?: AnalyticsChartPeriod
  ) => {
    this._rawStatistic = response.statistic
    this._rawChartData = response.charts
    if (period) {
      this._period = period
    }
  }

  get processedStatistic(): ProcessedStatistic[] {
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue } = item

      return {
        key: key,
        label: this.getDisplayLabel(key),
        value: value,
        prevValue: prevValue,
        trend: undefined,
      }
    })
  }

  get rechartsData(): CreditUsageRechartsDataPoint[] {
    return this._rawChartData
      .map((item) => ({
        date: item.date,
        name: formatDateForDisplay(item.date, this._period),
        total_credits: item.total_credits || 0,
        transcripts_credits: item.transcripts_credits || 0,
        chatbots_credits: item.chatbots_credits || 0,
        ai_calling_agent_credits: item.ai_calling_agent_credits || 0,
        calls_credits: item.calls_credits || 0,
        messages_credits: item.messages_credits || 0,
        rvm_credits: item.rvm_credits || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  private getDisplayLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
      total_credits: 'Total credits',
      transcripts_credits: 'Transcripts credits',
      chatbots_credits: 'Texting agent credits',
      ai_calling_agent_credits: 'Calling agent credits',
      calls_credits: 'Calls credits',
      messages_credits: 'Messages credits',
      rvm_credits: 'Rvm credits',
    }
    return labelMap[key] || key
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  dispose = () => {
    this._rawStatistic = []
    this._rawChartData = []
    this._period = 'week'
  }
}
